import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LocationIcon } from '../../../../ui/icons';
import { initializeGoogleServices, getPlacePredictions, getPlaceDetails } from '../utils/googlePlaces';
import type { PlacePrediction, CommuteDestination } from '../utils/commuteTypes';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import {
  commuteInputContainer,
  commuteInput,
  commuteIcon,
  autocompleteDropdown,
  autocompleteSuggestion,
  autocompleteSuggestionFocused,
  suggestionPrimary,
  suggestionSecondary,
  loadingSpinner,
  srOnly,
} from './commuteAutocomplete.styles';

type CommuteAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onDestinationSelect: (destination: CommuteDestination) => void;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
};

/**
 * CommuteAutocomplete - Google Places autocomplete for commute destinations
 *
 * Provides real-time search suggestions using Google Places API.
 * Shows dropdown with place predictions and handles selection.
 */
export const CommuteAutocomplete: React.FC<CommuteAutocompleteProps> = ({
  value,
  onChange,
  onDestinationSelect,
  placeholder = 'Add a destination',
  disabled = false,
  'data-testid': testId,
}) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isServicesReady, setIsServicesReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [keyboardMode, setKeyboardMode] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const servicesRef = useRef<{
    autocompleteService: google.maps.places.AutocompleteService;
    geocoder: google.maps.Geocoder;
  } | null>(null);

  // Get Google Maps API key from Redux store
  const googleMapsKey = useSelector((state: RootReduxState) => state.app.googleMaps?.key);

  // Initialize Google services
  useEffect(() => {
    const initServices = async () => {
      if (!googleMapsKey || isServicesReady) return;

      try {
        const services = await initializeGoogleServices(googleMapsKey);
        servicesRef.current = services;
        setIsServicesReady(true);
        setError(null);
      } catch (err) {
        console.error('Failed to initialize Google services:', err);
        setError('Failed to load autocomplete service');
      }
    };

    initServices();
  }, [googleMapsKey, isServicesReady]);

  // Debounced search function
  const searchPlaces = useCallback(async (searchInput: string) => {
    if (!servicesRef.current || !searchInput.trim()) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await getPlacePredictions(servicesRef.current.autocompleteService, searchInput);
      setPredictions(results);
      // Reset keyboard navigation when new results arrive
      setFocusedIndex(-1);
    } catch (err) {
      console.error('Places search error:', err);
      setError('Search failed. Please try again.');
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce search requests
  useEffect(() => {
    if (!value.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchPlaces(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [value, searchPlaces]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowDropdown(true);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // Handle prediction selection
  const handlePredictionSelect = async (prediction: PlacePrediction) => {
    if (!servicesRef.current) return;

    setIsLoading(true);
    setShowDropdown(false);

    try {
      const destination = await getPlaceDetails(
        servicesRef.current.geocoder,
        prediction.placeId,
        prediction.description,
      );

      onChange(prediction.description);
      onDestinationSelect(destination);

      // Reset keyboard state after successful selection
      setFocusedIndex(-1);
      setKeyboardMode(false);
    } catch (err) {
      console.error('Failed to get place details:', err);
      setError('Failed to select location. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle mouse interaction with suggestions
  const handleMouseEnter = (index: number) => {
    if (!keyboardMode) {
      setFocusedIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!keyboardMode) {
      setFocusedIndex(-1);
    }
  };

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation with full arrow key support
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (predictions.length === 0) return;

        // If dropdown is closed but we have results, reopen it
        if (!showDropdown) {
          setShowDropdown(true);
          setFocusedIndex(0); // Focus first item
          setKeyboardMode(true);
        } else {
          // If dropdown is open, navigate normally
          setKeyboardMode(true);
          setFocusedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev));
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (predictions.length === 0) return;

        // If dropdown is closed but we have results, reopen it and focus last item
        if (!showDropdown) {
          setShowDropdown(true);
          setFocusedIndex(predictions.length - 1); // Focus last item
          setKeyboardMode(true);
        } else {
          // If dropdown is open, navigate normally
          setKeyboardMode(true);
          if (focusedIndex <= 0) {
            setFocusedIndex(-1);
            inputRef.current?.focus();
          } else {
            setFocusedIndex((prev) => prev - 1);
          }
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && predictions[focusedIndex]) {
          handlePredictionSelect(predictions[focusedIndex]);
        }
        break;

      case 'Escape':
        // Only close dropdown if it's open - don't let event bubble up to spoke
        if (showDropdown) {
          e.preventDefault(); // Prevent default browser behavior
          e.stopPropagation(); // Prevent event from bubbling up to spoke
          setShowDropdown(false);
          setFocusedIndex(-1);
          setKeyboardMode(false);
          // Keep focus on input - don't blur
        }
        // If dropdown is already closed, let Escape bubble up to close spoke
        break;

      case 'Tab':
        // Allow tab to move focus away from input
        setShowDropdown(false);
        setFocusedIndex(-1);
        setKeyboardMode(false);
        break;
    }
  };

  const shouldShowDropdown = showDropdown && !disabled && (predictions.length > 0 || isLoading || error);

  return (
    <div ref={containerRef} className={commuteInputContainer} data-testid={testId}>
      <div className={commuteIcon}>
        <LocationIcon />
      </div>
      <input
        ref={inputRef}
        className={commuteInput}
        type="text"
        placeholder={isServicesReady ? placeholder : 'Loading...'}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled || !isServicesReady}
        aria-label="Enter destination for commute calculation"
        aria-expanded={shouldShowDropdown ? true : false}
        aria-haspopup="listbox"
        aria-activedescendant={
          focusedIndex >= 0 && predictions[focusedIndex] ? `suggestion-${predictions[focusedIndex].placeId}` : undefined
        }
        aria-describedby={error ? 'commute-error' : undefined}
        autoComplete="off"
      />

      {shouldShowDropdown && (
        <div className={autocompleteDropdown} role="listbox" aria-label="Place suggestions">
          {isLoading && (
            <div className={loadingSpinner} data-testid="commute-autocomplete-loading">
              Searching...
            </div>
          )}

          {error && (
            <div
              id="commute-error"
              role="alert"
              className={autocompleteSuggestion}
              data-testid="commute-autocomplete-error"
            >
              {error}
            </div>
          )}

          {!isLoading &&
            !error &&
            predictions.map((prediction, index) => (
              <button
                key={prediction.placeId}
                id={`suggestion-${prediction.placeId}`}
                className={`${autocompleteSuggestion} ${focusedIndex === index ? autocompleteSuggestionFocused : ''}`}
                onClick={() => handlePredictionSelect(prediction)}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                role="option"
                aria-selected={focusedIndex === index}
                tabIndex={focusedIndex === index ? 0 : -1}
                data-testid={`commute-suggestion-${index}`}
              >
                <div className={suggestionPrimary}>{prediction.primaryText}</div>
                {prediction.secondaryText && <div className={suggestionSecondary}>{prediction.secondaryText}</div>}
              </button>
            ))}
        </div>
      )}

      {/* Screen reader announcements for dynamic content changes */}
      {shouldShowDropdown && (
        <div role="status" aria-live="polite" aria-atomic="true" className={srOnly}>
          {isLoading && 'Searching for places...'}
          {!isLoading &&
            !error &&
            predictions.length > 0 &&
            `${predictions.length} ${predictions.length === 1 ? 'suggestion' : 'suggestions'} available. Use arrow keys to navigate.`}
          {!isLoading && !error && predictions.length === 0 && value.trim() && 'No suggestions found.'}
          {error && `Error: ${error}`}
        </div>
      )}
    </div>
  );
};
